// src/pages/Tools/nintendo-quest/AchievementBadges.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { Award, Lock, Star } from 'lucide-react';
import { Achievements } from '../../../lib/nintendo-quest/types';

interface AchievementBadgesProps {
  achievements: Achievements;
  onAchievementClick?: () => void;
}

export default function AchievementBadges({ 
  achievements, 
  onAchievementClick 
}: AchievementBadgesProps) {
  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Award className="w-6 h-6 text-yellow-400" />
          Achievements
        </h3>
        <div className="text-sm text-white/60">
          {achievements.unlocked.length} unlocked
        </div>
      </div>

      {/* Unlocked Achievements */}
      {achievements.unlocked.length > 0 && (
        <div className="mb-6">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-400" />
            Unlocked
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {achievements.unlocked.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onAchievementClick}
                className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/30 rounded-lg p-4 cursor-pointer hover:from-yellow-500/30 hover:to-orange-500/30 transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{achievement.icon}</div>
                  <div className="flex-1">
                    <div className="text-white font-bold">{achievement.name}</div>
                    <div className="text-yellow-200 text-sm">Completed!</div>
                  </div>
                  <div className="text-yellow-400">
                    <Award className="w-6 h-6" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Available Achievements */}
      {achievements.available.length > 0 && (
        <div>
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-gray-400" />
            In Progress
          </h4>
          <div className="grid grid-cols-1 gap-3">
            {achievements.available.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: (achievements.unlocked.length + index) * 0.1 }}
                className="bg-gray-500/10 border border-gray-400/20 rounded-lg p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl grayscale">{achievement.icon}</div>
                  <div className="flex-1">
                    <div className="text-white font-bold">{achievement.name}</div>
                    <div className="text-gray-300 text-sm">
                      {achievement.pointsNeeded} more points needed
                    </div>
                  </div>
                  <div className="text-gray-400">
                    <Lock className="w-5 h-5" />
                  </div>
                </div>
                
                {/* Progress bar for available achievements */}
                <div className="mt-3">
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.max(0, Math.min(100, 100 - (achievement.pointsNeeded / 100) * 100))}%` 
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {achievements.unlocked.length === 0 && achievements.available.length === 0 && (
        <div className="text-center py-8">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <div className="text-gray-300">No achievements yet</div>
          <div className="text-gray-400 text-sm">Keep working to unlock badges!</div>
        </div>
      )}
    </div>
  );
}